import { useMiningContext } from '../contexts/MiningContext';
import { calculateWinRate, calculateROI } from '../utils/calculations';

/**
 * useMining Hook - Simplified mining interface
 *
 * Provides easy access to mining state and functions with
 * computed statistics. Abstracts the mining context for
 * easier component usage.
 *
 * This hook can be easily modified to change how components
 * interact with the mining system.
 */
export function useMining() {
    const {
        miningState,
        selectedBlocks,
        deployToBlocks,
        claimRewards,
        toggleBlockSelection,
        clearBlockSelection,
        resetMiningState,
        deployedBlocks,
    } = useMiningContext();

    /**
     * Calculate win rate
     */
    const winRate = calculateWinRate(miningState.roundHistory);

    /**
     * Calculate ROI
     */
    const roi = calculateROI(miningState.totalInvested, miningState.totalMined * 0.1);

    /**
     * Get recent rounds (formatted for display)
     */
    const recentRounds = miningState.roundHistory.map(round => ({
        roundNumber: round.roundNumber,
        winningBlock: round.winningBlock,
        won: round.userWon,
        solReward: round.solReward,
        minerReward: round.minerReward,
        motherlodeHit: round.motherlodeHit,
        motherlodeReward: round.motherlodeReward,
    }));

    /**
     * Check if user can deploy
     */
    const canDeploy = selectedBlocks.size > 0;

    return {
        // Mining state
        currentRound: miningState.currentRound,
        timeLeft: miningState.timeLeft,
        lastWinningBlock: miningState.lastWinningBlock,
        motherlodePot: miningState.motherlodePot,

        // User balances
        unrefinedMINER: miningState.unrefinedMINER,
        refinedMINER: miningState.refinedMINER,
        pendingSOLRewards: miningState.pendingSOLRewards,

        // Block selection
        selectedBlocks,
        deployedBlocks,

        // Statistics
        winRate: winRate.winRate,
        totalMined: miningState.totalMined,
        roi,

        // Recent activity
        recentRounds,

        // Actions
        deployToBlocks,
        claimRewards,
        toggleBlockSelection,
        clearBlockSelection,
        resetMiningState,
        canDeploy,

        // Flags
        isActive: miningState.isActive,
    };
}
